import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SatisfactionsMiscDomainService } from '@apps/surv/domain';
import { CopilsMiscApiService } from '../api/copils-misc-api.service';
import { Copil, DomainCopil } from './models/copil';
import { Satisfaction } from '@apps/surv/domain/models';
import { EntitiesListService } from '@apps/auth/domain';
import { Entity, User } from '@apps/auth/domain/models/back';
import { CopilApi } from '../api/models/copil-api';
import { DetailedCopil } from './models/detailed-copil';
import { IAuroraState } from '@apps/aurora.state';
import { getAllCopils } from '../store/copils/copils-selectors';
import { UsersMiscDomainService } from '@apps/auth/domain/users-misc-domain.service';
import { ShortProfile } from '@apps/auth/domain/models';
import { CopilsSummary } from '@apps/dash/domain/models/copils-summary';
import { getProfile } from '@apps/auth/store';
import { getRootImpersonation } from '@apps/root/store';
import { getAllUsersInternal } from '@apps/back/store/auth/users/internal/users-internal.selectors';

@Injectable({
    providedIn: 'root'
})
export class CopilsMiscDomainService
{
    constructor (
        private store: Store<IAuroraState>,
        private copilsApiService: CopilsMiscApiService,
        private satisfactionsService: SatisfactionsMiscDomainService,
        private entitiesListService: EntitiesListService,
        private usersMiscDomainService : UsersMiscDomainService
    )
    { }

    getUserEntityCopils (): Observable<Copil[]>
    {
        return combineLatest([
            this.store.select(getProfile),
            this.store.select(getRootImpersonation)
        ])
            .pipe(
                map(([profile, impersonation]) =>
                    impersonation !== null ? impersonation.entityErpShortId : profile.entityErpShortId
                ),
                switchMap(entityErpShortId =>
                    combineLatest([
                        this.copilsApiService.getCopilsByEntityShortId(entityErpShortId),
                        this.store.select(getAllUsersInternal),
                        this.satisfactionsService.getUserEntitySatisfactions()
                    ])
                        .pipe(
                            map(([copilsApi, users, satisfactions]: [CopilApi[], User[], Satisfaction[]]) =>
                                copilsApi.map(c =>
                                    DomainCopil.mapperFromApi(
                                        c,
                                        users,
                                        satisfactions
                                    )
                                )
                            )
                        )
                )
            );
    }

    getAllCopils (): Observable<Copil[]>
    {
        return combineLatest([
            this.copilsApiService.getAllCopils(),
            this.entitiesListService.getEntities('?external=true'),
            this.store.select(getAllUsersInternal),
            this.satisfactionsService.getAllSatisfactions()
        ])
            .pipe(
                map(([copilsApi, entities, users, satisfactions]: [CopilApi[], Entity[], User[], Satisfaction[]]) =>
                    copilsApi.map(c =>
                    {
                        const entity: Entity = entities.find(e => e.entityErpId === c.entityErpId);

                        return DomainCopil.mapperFromApi(
                            c,
                            users,
                            satisfactions.filter(s => s.entityId === entity?.id)
                        );
                    }))
            );
    }

    getDetailedCopil (copilId: string): Observable<DetailedCopil>
    {
        return this.store.select(getAllCopils)
            .pipe(
                map(copils => copils.find(c => c.copilId === copilId)),
                switchMap((copil: Copil) =>
                    this.usersMiscDomainService.shortProfile(copil?.governorUserName)
                        .pipe(
                            map((profile: ShortProfile) =>
                                Object.assign(new DetailedCopil(profile), copil)
                            ),
                            catchError((error) =>
                            {
                                if (error.status === 404)
                                    return of(Object.assign(new DetailedCopil(null), copil));

                                throwError(() => error);
                            })
                        )
                )
            );
    }

    getCopilsSummary (): Observable<CopilsSummary>
    {
        return combineLatest([
            this.store.select(getProfile),
            this.store.select(getRootImpersonation)
        ])
            .pipe(
                map(([profile, impersonation]) =>
                    impersonation !== null ? impersonation.entityErpShortId : profile.entityErpShortId
                ),
                switchMap((entityErpShortId) =>
                    this.copilsApiService.getCopilsSummaryByEntityShortId(entityErpShortId)
                        .pipe(
                            map(CopilsSummary.mapperFromApi)
                        )
                )
            );
    }
}
